import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Lightweight, app-agnostic toast/notification stack. Mirrors the surface
 * area of the old app's Ant Design `notification.open()` API but built on
 * a portal + role="status" so it works without a heavy dependency.
 *
 * Each notification auto-dismisses after `durationMs` (default 3500). Pass
 * `durationMs: 0` to make it sticky until the user closes it.
 */

export type NotificationVariant = 'success' | 'error' | 'info'

export type NotificationInput = {
  variant?: NotificationVariant
  title: string
  body?: ReactNode
  durationMs?: number
}

type StoredNotification = NotificationInput & { id: string }

type Ctx = {
  notify: (n: NotificationInput) => string
  dismiss: (id: string) => void
}

const NotificationContext = createContext<Ctx | null>(null)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<StoredNotification[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
    const t = timersRef.current.get(id)
    if (t) {
      clearTimeout(t)
      timersRef.current.delete(id)
    }
  }, [])

  const notify = useCallback(
    (n: NotificationInput): string => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      setItems((prev) => [...prev, { ...n, id }])
      const dur = n.durationMs ?? 3500
      if (dur > 0) {
        const t = setTimeout(() => dismiss(id), dur)
        timersRef.current.set(id, t)
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const map = timersRef.current
    return () => {
      map.forEach((t) => clearTimeout(t))
      map.clear()
    }
  }, [])

  const portalTarget = typeof document !== 'undefined' ? document.body : null

  return (
    <NotificationContext.Provider value={{ notify, dismiss }}>
      {children}
      {portalTarget
        ? createPortal(
            <ul
              className="al-toast-stack"
              role="region"
              aria-label="Notifications"
              aria-live="polite"
            >
              {items.map((item) => (
                <li
                  key={item.id}
                  className={`al-toast al-toast--${item.variant ?? 'info'}`}
                >
                  <div className="al-toast__body">
                    <p className="al-toast__title">{item.title}</p>
                    {item.body ? <div className="al-toast__content">{item.body}</div> : null}
                  </div>
                  <button
                    type="button"
                    className="al-toast__close"
                    aria-label="Dismiss notification"
                    onClick={() => dismiss(item.id)}
                  >
                    {/* Same circle-X used by the cart's remove button — keeps
                        the dismiss affordance consistent across the app. */}
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 38 38"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M19 38C8.5 38 0 29.5 0 19S8.5 0 19 0s19 8.5 19 19-8.5 19-19 19zm0-36.5C9.4 1.5 1.5 9.4 1.5 19S9.4 36.5 19 36.5 36.5 28.7 36.5 19 28.7 1.5 19 1.5z" />
                      <path d="M24.2 24.9c-.2 0-.4-.1-.5-.2L13.3 14.4c-.3-.3-.3-.8 0-1.1s.8-.3 1.1 0l10.3 10.3c.3.3.3.8 0 1.1-.1.1-.3.2-.5.2z" />
                      <path d="M13.9 24.9c-.2 0-.4-.1-.5-.2-.3-.3-.3-.8 0-1.1l10.3-10.3c.3-.3.8-.3 1.1 0s.3.8 0 1.1L14.4 24.7c-.2.1-.4.2-.5.2z" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>,
            portalTarget,
          )
        : null}
    </NotificationContext.Provider>
  )
}

// Fallback used when a consumer calls `useNotification()` outside the
// provider tree (e.g. unit tests that don't care about the toast layer).
// Mirrors the shape of the real context so callers never branch on its
// presence; the consequence of using it is just that notifications are
// silently dropped.
const NOOP_CTX: Ctx = {
  notify: () => '',
  dismiss: () => {},
}

export function useNotification(): Ctx {
  return useContext(NotificationContext) ?? NOOP_CTX
}
